import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Upload, Search, Star, Briefcase, Sparkles, Github } from 'lucide-react';

export default function LandingPage() {
    const { login } = useAuth();

    return (
        <div className="min-h-screen bg-void page-enter">
            {/* Noise overlay */}
            <div className="noise-overlay" />

            {/* Hero Section */}
            <div className="relative overflow-hidden">
                {/* Background image with overlay */}
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-20"
                    style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1737505599162-d9932323a889?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-void/80 via-void/90 to-void" />

                {/* Nav */}
                <nav className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-acid rounded-sm flex items-center justify-center">
                            <span className="text-black font-display font-bold text-lg">N</span>
                        </div>
                        <span className="font-display text-xl text-white">NODE HUNTER</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <a 
                            href="/client/login"
                            className="text-muted-foreground hover:text-cyber-purple transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            Employers
                        </a>
                        <Button 
                            data-testid="login-btn"
                            onClick={login}
                            className="bg-acid text-black font-mono uppercase tracking-wider text-sm px-6 py-2 rounded-sm hover:bg-acid-hover transition-all"
                        >
                            Sign In
                        </Button>
                    </div>
                </nav>

                {/* Hero Content */}
                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-void-paper border border-border mb-8">
                            <Sparkles className="w-4 h-4 text-acid" strokeWidth={1.5} />
                            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                                AI-Powered Job Matching
                            </span>
                        </div>

                        <h1 className="font-display text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
                            Hunt Your Next
                            <span className="block text-acid">Web3 Role</span>
                        </h1>

                        <p className="text-lg lg:text-xl text-muted-foreground max-w-xl mb-10 leading-relaxed">
                            Upload your CV. Get AI-analyzed skill matching. Discover blockchain, DeFi, and crypto positions tailored to your experience.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button 
                                data-testid="get-started-btn"
                                onClick={login}
                                className="bg-acid text-black font-mono uppercase tracking-wider text-sm px-8 py-4 rounded-sm hover:bg-acid-hover transition-all flex items-center gap-2"
                            >
                                Get Started <ArrowRight className="w-4 h-4" />
                            </Button>
                            <Button 
                                variant="outline"
                                className="bg-transparent border border-border text-white font-mono uppercase tracking-wider text-sm px-8 py-4 rounded-sm hover:border-acid hover:text-acid transition-all"
                            >
                                Learn More
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Grid */}
            <section className="py-20 lg:py-32 px-6 lg:px-12">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="font-display text-3xl lg:text-4xl font-bold text-white mb-4">
                            How It Works
                        </h2>
                        <p className="text-muted-foreground max-w-lg mx-auto">
                            Three simple steps to find your perfect Web3 position
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Feature 1 */}
                        <div className="bg-void-paper border border-border p-8 rounded-sm card-hover group">
                            <div className="w-12 h-12 bg-acid/10 border border-acid/30 rounded-sm flex items-center justify-center mb-6 group-hover:bg-acid/20 transition-colors">
                                <Upload className="w-6 h-6 text-acid" strokeWidth={1.5} />
                            </div>
                            <h3 className="font-display text-xl font-bold text-white mb-3">
                                Upload Your CV
                            </h3>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                                Drop your PDF or DOCX resume. Our AI instantly parses and extracts your skills, experience, and qualifications.
                            </p>
                        </div>

                        {/* Feature 2 */}
                        <div className="bg-void-paper border border-border p-8 rounded-sm card-hover group">
                            <div className="w-12 h-12 bg-cyber-purple/10 border border-cyber-purple/30 rounded-sm flex items-center justify-center mb-6 group-hover:bg-cyber-purple/20 transition-colors">
                                <Sparkles className="w-6 h-6 text-cyber-purple" strokeWidth={1.5} />
                            </div>
                            <h3 className="font-display text-xl font-bold text-white mb-3">
                                AI Analysis
                            </h3>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                                GPT-5.2 analyzes your profile, identifying key skills in Solidity, DeFi, smart contracts, and blockchain technologies.
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div className="bg-void-paper border border-border p-8 rounded-sm card-hover group">
                            <div className="w-12 h-12 bg-neon-cyan/10 border border-neon-cyan/30 rounded-sm flex items-center justify-center mb-6 group-hover:bg-neon-cyan/20 transition-colors">
                                <Search className="w-6 h-6 text-neon-cyan" strokeWidth={1.5} />
                            </div>
                            <h3 className="font-display text-xl font-bold text-white mb-3">
                                Job Matching
                            </h3>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                                We search ATS systems for Web3 jobs and rank them by match score based on your skills and experience.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 px-6 lg:px-12 border-y border-border">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        <div className="text-center">
                            <div className="font-display text-4xl lg:text-5xl font-bold text-acid mb-2">50K+</div>
                            <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Web3 Jobs Indexed</div>
                        </div>
                        <div className="text-center">
                            <div className="font-display text-4xl lg:text-5xl font-bold text-acid mb-2">95%</div>
                            <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Match Accuracy</div>
                        </div>
                        <div className="text-center">
                            <div className="font-display text-4xl lg:text-5xl font-bold text-acid mb-2">10s</div>
                            <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Analysis Time</div>
                        </div>
                        <div className="text-center">
                            <div className="font-display text-4xl lg:text-5xl font-bold text-acid mb-2">Free</div>
                            <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">To Get Started</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 lg:py-32 px-6 lg:px-12">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-display text-3xl lg:text-5xl font-bold text-white mb-6">
                        Ready to Find Your Next
                        <span className="text-acid"> Web3 Opportunity?</span>
                    </h2>
                    <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
                        Join thousands of blockchain developers, DeFi engineers, and crypto professionals finding their dream roles.
                    </p>
                    <Button 
                        data-testid="cta-btn"
                        onClick={login}
                        className="bg-acid text-black font-mono uppercase tracking-wider text-sm px-12 py-4 rounded-sm hover:bg-acid-hover transition-all glow-acid"
                    >
                        Start Hunting
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-border py-12 px-6 lg:px-12">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-acid rounded-sm flex items-center justify-center">
                            <span className="text-black font-display font-bold text-sm">N</span>
                        </div>
                        <span className="font-display text-sm text-white">NODE HUNTER</span>
                    </div>
                    <div className="flex items-center gap-8">
                        <a href="#" className="text-muted-foreground hover:text-acid transition-colors font-mono text-xs uppercase tracking-wider">
                            Privacy
                        </a>
                        <a href="#" className="text-muted-foreground hover:text-acid transition-colors font-mono text-xs uppercase tracking-wider">
                            Terms
                        </a>
                        <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-acid transition-colors">
                            <Github className="w-5 h-5" strokeWidth={1.5} />
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
